/**
 * Client-side authentication utilities.
 *
 * Tokens are stored in HttpOnly + Secure + SameSite cookies set by the server,
 * so JavaScript (including any XSS payload) can never read or exfiltrate them.
 *
 * As a result:
 *   - setToken / getToken / clearToken are no longer used for token storage.
 *     setToken and clearToken are kept as no-ops for backward compatibility
 *     during the transition, and will be removed in a future cleanup pass.
 *   - fetchWithAuth uses `credentials: "include"` so the browser automatically
 *     attaches the auth cookie to every API request.
 *   - isAuthenticated checks a lightweight sessionStorage flag set after a
 *     successful login or SSO handshake, since we can no longer read the token
 *     from JS. The flag is cleared on logout and on a fresh browser session.
 *   - Logout calls POST /auth/logout, which revokes the refresh token in the DB
 *     and clears both auth cookies server-side.
 *   - Token refresh is handled by a silent POST /auth/token/refresh call (rotate
 *     the refresh token, issue a new access token) which the WorkspaceContext
 *     schedules before the 1-hour access token expires.
 */

export const API_BASE = (typeof window !== "undefined")
  ? ""
  : (process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001");

/** @deprecated Tokens are now in HttpOnly cookies — no longer stored in JS. No-op kept for compatibility. */
export function setToken(_token: string): void { /* intentional no-op */ }

/** @deprecated Returns null — token is in an HttpOnly cookie, not readable by JS. */
export function getToken(): string | null { return null; }

/** @deprecated Use logout() in WorkspaceContext instead. No-op kept for compatibility. */
export function clearToken(): void { /* intentional no-op */ }

const AUTH_FLAG_KEY = "cmthub_authenticated";

/** Who this tab believes it is signed in as. */
const IDENTITY_KEY = "cmthub_identity";

/**
 * Fired when this tab's session is gone: the cookie was refused and a silent
 * refresh could not save it, so every later request 401s.
 *
 * `WorkspaceContext` listens and re-hydrates rather than letting a component
 * discover it by crashing — which is what used to produce a white screen on a
 * deep link opened against a dead session.
 *
 * The sibling case, someone signing in as a DIFFERENT user in another tab, does
 * not need an event: auth cookies are shared across tabs, so nothing fails here
 * until this tab asks. `assertSameIdentity` reports it on the next profile
 * fetch, which `WorkspaceContext` runs when the tab is brought back into view.
 */
export const SESSION_CHANGED_EVENT = "cmta:session-changed";

export type SessionChangeReason = "expired";

function announceSessionChange(reason: SessionChangeReason): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(SESSION_CHANGED_EVENT, { detail: { reason } }));
}

/** Remember which user this tab is showing, so a swap can be detected. */
export function markIdentity(userId: string | null | undefined): void {
    if (typeof window === "undefined") return;
    if (userId) sessionStorage.setItem(IDENTITY_KEY, String(userId));
    else sessionStorage.removeItem(IDENTITY_KEY);
}

export function currentIdentity(): string | null {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem(IDENTITY_KEY);
}

/**
 * Compare a freshly fetched user against the one this tab was showing, and
 * re-stamp. Returns true when the identity changed.
 *
 * Reports rather than dispatches: the only caller is `WorkspaceContext`, which
 * is the thing that would react to an event anyway — announcing here would just
 * make it re-enter its own listener and fetch the profile a second time.
 */
export function assertSameIdentity(user: { id?: string | number } | null): boolean {
    if (typeof window === "undefined") return false;
    const seen = currentIdentity();
    const now = user?.id != null ? String(user.id) : null;
    markIdentity(now);
    return !!(seen && now && seen !== now);
}

/** Set the lightweight in-session auth marker after a successful login / SSO. */
export function markAuthenticated(): void {
    if (typeof window !== "undefined") sessionStorage.setItem(AUTH_FLAG_KEY, "1");
}

/** Clear the auth marker (called on logout). */
export function clearAuthMark(): void {
    if (typeof window !== "undefined") {
        sessionStorage.removeItem(AUTH_FLAG_KEY);
        sessionStorage.removeItem(IDENTITY_KEY);
    }
}

/** True while the session marker is present (approximate — does not decode the JWT). */
export function isAuthenticated(): boolean {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(AUTH_FLAG_KEY) === "1";
}

/**
 * Start the CMT Association SSO flow. Full-page navigation to the backend
 * /sso/login endpoint, which 302s to cmtassociation.org's OAuth server.
 */
export function startSso(next?: string): void {
    if (typeof window === "undefined") return;
    const qs = next ? `?next=${encodeURIComponent(next)}` : "";
    window.location.href = `/api/v1/auth/sso/login${qs}`;
}

export interface AuthResponse {
    token_type: string;
    must_change_password: boolean;
    is_profile_complete: boolean;
}

export async function login(email: string, password: string): Promise<AuthResponse> {
    const formData = new URLSearchParams();
    formData.append("username", email);
    formData.append("password", password);

    const res = await fetch(`${API_BASE}/api/v1/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
        credentials: "include",  // receive the Set-Cookie headers
    });

    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Login failed");
    }

    const data: AuthResponse = await res.json();
    markAuthenticated();
    return data;
}

// Signup and password-recovery flows were removed: members authenticate via
// SSO only; password login remains for staff test accounts.
// Password change is also disabled — passwords are managed by admins only.

/** Generate bio draft from LinkedIn URL. */
export async function generateBio(linkedinUrl: string): Promise<{ draft_bio: string; linkedin_snippet: string }> {
    const res = await fetchWithAuth(`${API_BASE}/api/v1/auth/onboarding/generate-bio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ linkedin_url: linkedinUrl }),
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Bio generation failed");
    }
    return res.json();
}

/** Mark onboarding complete. */
export async function finishOnboarding(): Promise<void> {
    const res = await fetchWithAuth(`${API_BASE}/api/v1/auth/onboarding/finish`, { method: "POST" });
    if (!res.ok) throw new Error("Failed to complete onboarding");
}

/** Record acceptance of the current Volunteer Code of Conduct (Tier 0). */
export async function acceptCoc(version: string): Promise<void> {
    const res = await fetchWithAuth(`${API_BASE}/api/v1/auth/onboarding/accept-coc`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ version }),
    });
    if (!res.ok) throw new Error("Failed to accept Code of Conduct");
}

/** Update profile fields (bio, linkedin, photo). */
export async function updateProfile(fields: {
    bio?: string;
    linkedin?: string;
    profile_photo_url?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    company?: string;
    job_title?: string;
    designations?: string;
    designation_other?: string;
    gender_self?: string;
}) {
    const res = await fetchWithAuth(`${API_BASE}/api/v1/auth/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
    });
    if (!res.ok) throw new Error("Profile update failed");
    return res.json();
}

/** Dev SSO bypass (restricted to @cmtassociation.org). */
export async function testLogin(email: string): Promise<AuthResponse> {
    const res = await fetch(`${API_BASE}/api/v1/auth/test-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        credentials: "include",  // receive the Set-Cookie headers
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Test login failed");
    }
    const data = await res.json();
    markAuthenticated();
    return data;
}

/**
 * Silent token refresh — rotate the refresh token and get a new access token.
 * Called automatically by WorkspaceContext before the 1-hour access token expires.
 * Returns true if the refresh succeeded, false if the session is dead.
 */
export async function silentRefresh(): Promise<boolean> {
    try {
        const res = await fetch(`${API_BASE}/api/v1/auth/token/refresh`, {
            method: "POST",
            credentials: "include",
        });
        if (res.ok || res.status === 204) {
            markAuthenticated();
            return true;
        }
        clearAuthMark();
        return false;
    } catch {
        return false;
    }
}

/**
 * Server-side logout — revoke the refresh token in the DB and clear both
 * auth cookies. WorkspaceContext calls this then redirects to /logged-out.
 */
export async function serverLogout(): Promise<void> {
    try {
        await fetch(`${API_BASE}/api/v1/auth/logout`, {
            method: "POST",
            credentials: "include",
        });
    } catch { /* swallow — cookies will expire naturally */ }
    clearAuthMark();
}

/**
 * Authenticated fetch. Sends the HttpOnly auth cookie automatically.
 * On 401, attempts one silent token refresh and retries the original request.
 * If the refresh also fails, clears the auth marker and lets the caller handle.
 */
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    // Captured before the 401 handling below clears it. A 401 only means a
    // session *died* if there was one to begin with; for a signed-out visitor
    // it is the ordinary answer, and announcing it would bounce them to /login
    // from /login. See announceSessionChange.
    const hadSession = isAuthenticated();
    const res = await fetch(url, { ...options, credentials: "include" });

    if (res.status === 401) {
        // Try to silently refresh the access token once
        const refreshed = await silentRefresh();
        if (refreshed) {
            // Retry the original request with fresh cookie. A second 401 means
            // the session is genuinely gone (or now belongs to someone else),
            // not that the token was merely stale — announce it rather than
            // handing back a response indistinguishable from any other failure.
            const retried = await fetch(url, { ...options, credentials: "include" });
            if (retried.status === 401) {
                clearAuthMark();
                if (hadSession) announceSessionChange("expired");
            }
            return retried;
        }
        // Refresh failed — session is dead. The caller still gets the 401 to
        // render, but the app is told so it can re-hydrate instead of leaving a
        // stale workspace on screen.
        clearAuthMark();
        if (hadSession) announceSessionChange("expired");
    }

    return res;
}

export async function getCurrentUser() {
    try {
        const res = await fetchWithAuth(`${API_BASE}/api/v1/auth/me`, {
            cache: "no-store",
            headers: {
                "Cache-Control": "no-cache, no-store, must-revalidate",
                "Pragma": "no-cache",
                "Expires": "0",
            },
        });
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}
